import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, RotateCcw } from "lucide-react";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";

import { APIError } from "@/adapters/api";
import { DatePicker } from "@/components/date-picker";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
	currentWorkDay,
	dateOnlyToDate,
	dateToDateOnly,
} from "@/lib/format.ts";
import { useClearActualDates } from "../../hooks/use-clear-actual-dates.ts";
import { useSetActualDates } from "../../hooks/use-set-actual-dates.ts";
import {
	type ActualDatesFormValues,
	actualDatesSchema,
} from "../../schemas/actual-dates-schema.ts";
import type { ScheduleNode } from "../../types.ts";

const VIOLATION_FIELDS: Record<string, keyof ActualDatesFormValues> = {
	actual_start_date: "actualStart",
	actual_end_date: "actualEnd",
};

const DESCRIPTION_MESSAGES: Record<string, string> = {
	required: "Campo obrigatório.",
	invalid: "Data inválida.",
	future: "A data não pode ser futura.",
	"gte:actual_start_date":
		"O término real deve ser igual ou posterior ao início real.",
	not_leaf: "Somente etapas sem sub-etapas têm realizado.",
	not_scheduled: "Defina o planejado antes do realizado.",
	not_found: "Etapa não encontrada.",
};

function describe(description: string): string {
	return DESCRIPTION_MESSAGES[description] ?? description;
}

export function ActualDatesDialog({
	open,
	onOpenChange,
	constructionId,
	node,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	constructionId: string;
	node: ScheduleNode | null;
}) {
	const [formError, setFormError] = useState<string | null>(null);
	const setActual = useSetActualDates(constructionId);
	const clearActual = useClearActualDates(constructionId);
	const isPending = setActual.isPending || clearActual.isPending;

	const {
		control,
		handleSubmit,
		reset,
		setError,
		watch,
		formState: { errors },
	} = useForm<ActualDatesFormValues>({
		resolver: zodResolver(actualDatesSchema),
		defaultValues: { actualStart: "", actualEnd: "" },
	});

	useEffect(() => {
		if (!open) return;
		reset({
			actualStart: node?.ownActualStart ?? "",
			actualEnd: node?.ownActualEnd ?? "",
		});
		setFormError(null);
	}, [open, node, reset]);

	const startValue = watch("actualStart");
	const minEndDate = dateOnlyToDate(startValue);
	const isManual = node?.actualSource === "manual";
	const isDerived = node?.actualSource === "derived" && !!node.ownActualStart;

	function applyAPIError(err: unknown, fallback: string) {
		if (err instanceof APIError) {
			let matched = false;
			for (const [field, description] of Object.entries(err.violations)) {
				const formField = VIOLATION_FIELDS[field];
				if (formField) {
					setError(formField, { message: describe(description) });
				} else {
					setFormError(describe(description));
				}
				matched = true;
			}
			if (!matched) setFormError(err.message || fallback);
			return;
		}
		setFormError(err instanceof Error ? err.message : fallback);
	}

	async function onSubmit(values: ActualDatesFormValues) {
		if (!node) return;
		setFormError(null);
		try {
			await setActual.mutateAsync({
				stageId: node.id,
				input: {
					actualStartDate: values.actualStart,
					actualEndDate: values.actualEnd || undefined,
				},
			});
			toast.success("Realizado atualizado com sucesso!");
			onOpenChange(false);
		} catch (err) {
			applyAPIError(err, "Não foi possível salvar o realizado.");
		}
	}

	async function handleClear() {
		if (!node) return;
		setFormError(null);
		try {
			await clearActual.mutateAsync(node.id);
			toast.success("Realizado voltou a ser calculado pelas medições.");
			onOpenChange(false);
		} catch (err) {
			applyAPIError(err, "Não foi possível recalcular o realizado.");
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent onOpenAutoFocus={(event) => event.preventDefault()}>
				<DialogHeader>
					<DialogTitle>Informar realizado</DialogTitle>
					<DialogDescription>
						{node ? `Período realmente executado de “${node.name}”.` : ""}
					</DialogDescription>
				</DialogHeader>
				<form
					id="actual-dates-form"
					className="actual-dates-form"
					onSubmit={handleSubmit(onSubmit)}
					noValidate
				>
					<div className="actual-dates-form__dates">
						<div className="form-group">
							<Label htmlFor="actual-start">Início real</Label>
							<Controller
								control={control}
								name="actualStart"
								render={({ field }) => (
									<DatePicker
										id="actual-start"
										name={field.name}
										modal
										selected={dateOnlyToDate(field.value)}
										onSelected={(date) => field.onChange(dateToDateOnly(date))}
										aria-invalid={!!errors.actualStart}
										calendarProps={{ disabled: { after: currentWorkDay() } }}
									/>
								)}
							/>
							{errors.actualStart && (
								<p className="form-error">{errors.actualStart.message}</p>
							)}
						</div>
						<div className="form-group">
							<Label htmlFor="actual-end">Término real (opcional)</Label>
							<Controller
								control={control}
								name="actualEnd"
								render={({ field }) => (
									<DatePicker
										id="actual-end"
										name={field.name}
										modal
										selected={dateOnlyToDate(field.value)}
										onSelected={(date) => field.onChange(dateToDateOnly(date))}
										aria-invalid={!!errors.actualEnd}
										calendarProps={{
											disabled: [
												{ after: currentWorkDay() },
												...(minEndDate ? [{ before: minEndDate }] : []),
											],
										}}
									/>
								)}
							/>
							{errors.actualEnd && (
								<p className="form-error">{errors.actualEnd.message}</p>
							)}
						</div>
					</div>

					{isDerived && (
						<p className="form-hint">
							Datas calculadas a partir das medições. Ao salvar, as datas
							informadas passam a valer.
						</p>
					)}

					{formError && (
						<p role="alert" className="form-error-alert">
							{formError}
						</p>
					)}
				</form>
				<DialogFooter className="actual-dates-form__footer">
					{isManual && (
						<Button
							type="button"
							variant="ghost"
							className="actual-dates-form__clear"
							onClick={handleClear}
							disabled={isPending}
						>
							<RotateCcw />
							Voltar a calcular pelas medições
						</Button>
					)}
					<div className="actual-dates-form__actions">
						<Button
							type="button"
							variant="ghost"
							onClick={() => onOpenChange(false)}
							disabled={isPending}
						>
							Cancelar
						</Button>
						<Button type="submit" form="actual-dates-form" disabled={isPending}>
							{setActual.isPending && (
								<Loader2 className="actual-dates-form__spinner" />
							)}
							Salvar
						</Button>
					</div>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
